<script setup lang="ts">
import { computed, inject, ref, watch } from "vue";
import {
  AnswerReviewFormElement,
  FormElementBase,
  FormElementType,
  FormResponseStatus,
} from "~/api";
import {
  type WorkflowState,
  workflowInjectKey,
} from "~/Compliance/composables/useWorkflow";
import {
  FormResponseState,
  formResponseInjectKey,
} from "../../composables/useFormResponse";

import { ArrowLeft, ArrowRight, Close } from "@element-plus/icons-vue";
import { plural } from "~/utils/miscUtils";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props = defineProps<DefaultFormComponentProps<AnswerReviewFormElement>>();
const elementProperties = computed(() => props.modelValue.properties);

const {
  useAnswer,
  formResponse,
  updateFormResponseStatus,
  // updateWorkflowStatus,
  substituteTemplatedText,
  getQuestionsRequiringAttention,
} = inject(
  formResponseInjectKey,
  // Cast the type since we know it is always provided. For more info see:
  // https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject
) as FormResponseState;
const { updateWorkflowStatus, getWorkflowStatus, getWorkflow } = inject(
  workflowInjectKey,
  {
    updateWorkflowStatus: async (status: string) => {
      console.log("No workflow in designer. Status to update to:", status);
    },
    updateWorkflowStatusFromNameStamp: async (status: string) => {
      console.log("No workflow in designer. Status to update to:", status);
    },
    getWorkflowStatus: () => {
      return "";
    },
    getWorkflow: () => {
      return undefined;
    },
  },
) as WorkflowState;

const answer = useAnswer<FormElementType.ANSWER_REVIEW>(props.modelValue);

const reviewInProgress = ref(false);
const currentIndex = ref<number>(0);
/** Store the sections remaining when the review started so that we can easily navigate through them */
const questionsRemainingWhenReviewStarted = ref<FormElementBase[]>([]);

const questionsRemaining = computed(() => {
  return getQuestionsRequiringAttention();
});

const handleStartReview = () => {
  reviewInProgress.value = true;
  currentIndex.value = 0;
};

const handleCloseReview = () => {
  reviewInProgress.value = false;
  scrollToElement(props.modelValue);
};

/** Scroll to the next unanswered question in the original list */
const handleGoToNextQuestion = () => {
  if (questionsRemainingWhenReviewStarted.value.length === 0) {
    reviewInProgress.value = false;
    return;
  } else if (questionsRemaining.value.length === 1) {
    // const guid = questionsRemaining.value[0].guid;
    scrollToElement(questionsRemaining.value[0]);
    return;
  }

  const prevQuestion =
    questionsRemainingWhenReviewStarted.value[currentIndex.value] ?? null;

  // move to the next question, wrapping around
  // modulus operation ensures that if currentIndex.value goes past the last question,
  // it wraps back around to 0
  currentIndex.value =
    (currentIndex.value + 1) % questionsRemainingWhenReviewStarted.value.length;

  // find the next unanswered question based on the original list
  let currentQuestion =
    questionsRemainingWhenReviewStarted.value[currentIndex.value] ?? null;

  while (
    currentQuestion &&
    !questionsRemaining.value.find((q) => q.guid === currentQuestion.guid)
  ) {
    currentIndex.value =
      (currentIndex.value + 1) %
      questionsRemainingWhenReviewStarted.value.length;
    currentQuestion =
      questionsRemainingWhenReviewStarted.value[currentIndex.value] ?? null;
  }

  if (!currentQuestion) {
    reviewInProgress.value = false;
    return;
  }

  scrollToElement(currentQuestion, prevQuestion);
};

/** Scroll to the previous unanswered question in the original list */
const handleGoToPrevQuestion = () => {
  if (questionsRemainingWhenReviewStarted.value.length === 0) {
    reviewInProgress.value = false;
    return;
  } else if (questionsRemaining.value.length === 1) {
    // const guid = questionsRemaining.value[0].guid;
    scrollToElement(questionsRemaining.value[0]);
    return;
  }

  const prevQuestion =
    questionsRemainingWhenReviewStarted.value[currentIndex.value] ?? null;

  // move to the previous question, wrapping around
  // modulus operation ensures that when currentIndex.value becomes negative,
  // it wraps around to the last question in the list
  currentIndex.value =
    (currentIndex.value -
      1 +
      questionsRemainingWhenReviewStarted.value.length) %
    questionsRemainingWhenReviewStarted.value.length;

  // find the previous unanswered question based on the original list
  let currentQuestion =
    questionsRemainingWhenReviewStarted.value[currentIndex.value] ?? null;
  while (
    currentQuestion &&
    !questionsRemaining.value.find((q) => q.guid === currentQuestion.guid)
  ) {
    currentIndex.value =
      (currentIndex.value -
        1 +
        questionsRemainingWhenReviewStarted.value.length) %
      questionsRemainingWhenReviewStarted.value.length;
    currentQuestion =
      questionsRemainingWhenReviewStarted.value[currentIndex.value] ?? null;
  }

  if (!currentQuestion) {
    reviewInProgress.value = false;
    return;
  }

  scrollToElement(currentQuestion, prevQuestion);
};

const scrollToElement = (
  question: FormElementBase,
  prevQuestion?: FormElementBase,
) => {
  const element = document.getElementById(
    question.guid + "-" + question.repeaterGuid,
  );

  if (element) {
    element.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "center",
    });

    element.classList.add("focus");
  }

  // remove focus class from the previously focused element
  if (prevQuestion?.guid) {
    document
      .getElementById(prevQuestion.guid + "-" + prevQuestion.repeaterGuid)
      ?.classList.remove("focus");
  }
};

const highlightUnansweredQuestions = () => {
  questionsRemainingWhenReviewStarted.value.forEach((question) => {
    /** Remove 'unanswered' class from answered questions */
    if (
      !questionsRemaining.value.some(
        (q) =>
          q.guid === question.guid && q.repeaterGuid === question.repeaterGuid,
      )
    ) {
      const element = document.getElementById(
        question.guid + "-" + question.repeaterGuid,
      );
      if (element) {
        element.classList.remove("unanswered");
        element.classList.remove("focus");
      }
    } else {
      /** Add 'unanswered' class to unanswered questions */
      const element = document.getElementById(
        question.guid + "-" + question.repeaterGuid,
      );

      if (element) {
        element.classList.add("unanswered");
      }
    }
  });

  /** Add 'unanswered' class to the questions with answers that were removed while in review mode */
  const newUnansweredQuestions = questionsRemaining.value.filter((value) => {
    return !questionsRemainingWhenReviewStarted.value.includes(value);
  });

  newUnansweredQuestions.forEach((question) => {
    const element = document.getElementById(
      question.guid + "-" + question.repeaterGuid,
    );

    if (element) {
      element.classList.add("unanswered");
    }
  });
  questionsRemainingWhenReviewStarted.value = questionsRemaining.value;
};

/** Add/remove a class from the form component elements based on wether or not we are in review mode */
watch(
  () => reviewInProgress.value,
  (newVal) => {
    if (newVal === true) {
      currentIndex.value = -1;
      questionsRemainingWhenReviewStarted.value = questionsRemaining.value;
      highlightUnansweredQuestions();

      // scroll straight to the first unanswered question
      handleGoToNextQuestion();
    } else {
      const elements = document.querySelectorAll(".unanswered");

      elements.forEach((element) => {
        element.classList.remove("unanswered");
        element.classList.remove("focus");
      });
    }
  },
);

const adjustedQuestionsRemainingText = computed(() =>
  substituteTemplatedText(elementProperties.value.questionsRemainingText),
);

const adjustedNoQuestionsRemainingText = computed(() =>
  substituteTemplatedText(elementProperties.value.noQuestionsRemainingText),
);

const adjustedStatusUpdatedText = computed(() =>
  substituteTemplatedText(elementProperties.value.statusUpdatedText),
);

const isStatusValidOrAbove = computed(() => {
  const statusList = getWorkflow()?.workflowDefinition?.statusList;

  if (!statusList) {
    console.warn(
      "No status list found. Cannot determine if the button should be disabled.",
    );
    return false;
  }
  const currentStatus = getWorkflowStatus();

  // if elementProperties.value.changeToStatus is <= currentStatus, then the button should be disabled
  const isDisabled =
    statusList.findIndex((status) => status.label === currentStatus) >=
    statusList.findIndex(
      (status) =>
        status.label === elementProperties.value.changeWorkflowStatusTo,
    );

  // formResponse?.status === FormResponseStatus.READY_FOR_SUBMISSION ||
  // formResponse?.status === FormResponseStatus.SUBMITTED
  return isDisabled;
});

const showConfirmButton = computed(() => {
  return (
    !elementProperties.value.hideConfirmButton &&
    (elementProperties.value.changeWorkflowStatusTo ||
      elementProperties.value.changeToStatus) &&
    !isStatusValidOrAbove.value
  );
});

watch(
  () => questionsRemaining.value,
  async () => {
    // update the answer value to reflect the number of questions remaining
    if (answer.value !== questionsRemaining.value.length) {
      answer.value = questionsRemaining.value.length;

      if (
        questionsRemaining.value.length > 0 &&
        isStatusValidOrAbove.value &&
        elementProperties.value.changeWorkflowStatusBackTo
      ) {
        await updateWorkflowStatus(
          elementProperties.value.changeWorkflowStatusBackTo,
        );
        await updateFormResponseStatus(
          elementProperties.value.changeBackToStatus ??
            FormResponseStatus.DRAFT,
        );
      }
    }
    if (reviewInProgress.value) {
      highlightUnansweredQuestions();
    } else if (
      /** put the form back in draft status if the user removes an answer */
      questionsRemaining.value.length > 0 &&
      formResponse?.status === FormResponseStatus.READY_FOR_SUBMISSION
    ) {
      updateFormResponseStatus(
        elementProperties.value.changeBackToStatus ?? FormResponseStatus.DRAFT,
      );
    }
  },
);

const handleConfirm = async () => {
  try {
    await updateFormResponseStatus(
      elementProperties.value.changeToStatus ?? FormResponseStatus.NEW,
    );
    if (elementProperties.value.changeWorkflowStatusTo) {
      await updateWorkflowStatus(
        elementProperties.value.changeWorkflowStatusTo,
      );
    }
    // Optionally show success message
  } catch (_err) {
    // Optionally show error message
  }
};
</script>

<template>
  <div id="AnswerReview" class="AnswerReview">
    <slot name="label" :element="modelValue" />
    <div v-if="questionsRemaining.length" class="review-section">
      <!-- eslint-disable-next-line vue/no-v-html  -->
      <div v-html="adjustedQuestionsRemainingText" />
      <el-button
        v-if="!reviewInProgress"
        type="info"
        @click="handleStartReview"
        >{{ elementProperties.reviewButtonText ?? "Review Answers" }}</el-button
      >
    </div>
    <div v-else class="review-section">
      <!-- eslint-disable vue/no-v-html -->
      <div v-if="isStatusValidOrAbove" v-html="adjustedStatusUpdatedText" />
      <div v-else v-html="adjustedNoQuestionsRemainingText" />
      <!-- eslint-enable vue/no-v-html -->

      <el-button
        v-if="showConfirmButton"
        class="signButton"
        type="primary"
        @click="handleConfirm"
      >
        {{ elementProperties.confirmButtonText ?? "Confirm" }}
      </el-button>
    </div>

    <el-affix v-if="reviewInProgress" position="bottom" :offset="-2">
      <div class="review-drawer">
        <el-button type="info" :icon="Close" text @click="handleCloseReview">
        </el-button>
        <div class="content">
          <span
            :title="questionsRemaining.map((q) => q.questionCode).join(', ')"
          >
            You have {{ questionsRemaining.length }} question{{
              plural(questionsRemaining.length)
            }}
            requiring attention.
          </span>
          <div class="buttons">
            <el-button-group>
              <el-button
                v-if="questionsRemaining.length"
                type="primary"
                :icon="ArrowLeft"
                @click="
                  () => {
                    handleGoToPrevQuestion();
                  }
                "
              >
                Previous
              </el-button>
              <el-button
                v-if="questionsRemaining.length"
                type="primary"
                @click="
                  () => {
                    handleGoToNextQuestion();
                  }
                "
              >
                Next
                <el-icon class="el-icon--right"><ArrowRight /></el-icon>
              </el-button>
            </el-button-group>
          </div>
        </div>
      </div>
    </el-affix>
  </div>
</template>

<style lang="less">
.unanswered {
  background-color: var(--el-color-danger-light-8);
  &.focus {
    background-color: var(--el-color-danger-light-5);
  }
}

.AnswerReview {
  .review-section {
    display: grid;
    gap: 1em;
    margin-bottom: 16px;
    .el-button {
      justify-self: end;
      text-wrap: nowrap;
      height: fit-content;
    }
  }
  .review-drawer {
    display: flex;
    justify-content: space-between;
    // gap: 1em;
    padding: 1em 0.5em;
    margin: 0 -16px -11px -24px;
    height: max-content;
    background-color: var(--el-color-info);
    color: white;

    .el-button.is-text:not(.is-disabled):hover {
      background-color: transparent;
    }

    .el-icon {
      --color: white;
    }

    .content {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 0.5em;
      width: 100%;
      padding-right: 8px;
      .buttons {
        // align-self: flex-end;
        margin-left: auto;
        .el-button-group .el-button--primary:first-child {
          width: 150px;
          padding-left: 10px;
        }
        .el-button-group .el-button--primary:last-child {
          width: 130px;
          padding-left: 25px;
        }
      }
    }
  }
}
</style>
